import { DivConst } from 'constants/divConst';

import type { VoiceChatEditFormStateType } from 'types/page/voiceChatEditFormStateType';
import { isEmpty } from 'utils/app';

/**
 * HTML フィールドが実質空かどうかを判定する。
 * タグを除去したテキストが空の場合、または値が null / undefined / 空文字の場合に true を返す。
 */
export function isHtmlFieldEmpty(html: string | null | undefined): boolean {
  if (html === null || html === undefined || html === '') {
    return true;
  }
  const text = html.replace(/<[^>]*>/g, '');
  return isEmpty(text);
}

/**
 * respondentFields の中に getFlag=true のフィールドが 1 件以上あるかを返す。
 */
export function hasAnyRespondentFieldGetFlag(values: VoiceChatEditFormStateType): boolean {
  return values.respondentFields.some(f => f.getFlag);
}

/**
 * ボイスチャット全体の必須バリデーションを実行し、エラー情報の配列を返す。
 * エラーがない場合は空配列を返す。
 * form の collectRequiredErrors をミラーしつつ、voice_chat 固有の差分（設問はタイトルのみ必須・選択肢/LINEなし・
 * 完了ページは単一 completeDescriptionHtml）を反映する。
 */
export function collectRequiredErrors(
  values: VoiceChatEditFormStateType
): Array<{ name: string; message: string }> {
  const errors: Array<{ name: string; message: string }> = [];

  if (isEmpty(values.privacyPolicyUrl)) {
    errors.push({ name: 'privacyPolicyUrl', message: 'プライバシーポリシーURLを入力してください' });
  }

  if (isEmpty(values.description)) {
    errors.push({ name: 'description', message: '説明文を入力してください' });
  }

  if (isEmpty(values.voiceChatTemplateId)) {
    errors.push({ name: 'voiceChatTemplateId', message: 'テンプレートを選択してください' });
  }

  // 回答者メール＆フィールド
  const { voiceChatRespondentMail } = DivConst.items;
  if (
    values.respondentMailDiv !== voiceChatRespondentMail.none.value &&
    !isEmpty(values.respondentMailDiv) &&
    !hasAnyRespondentFieldGetFlag(values)
  ) {
    errors.push({
      name: 'respondentMailDiv',
      message: '回答者情報の取得項目を 1 件以上選択してください',
    });
  }

  // 設問数（上限 10 問。回答モードを問わない総数。11 問以上の既存コンテンツは次回保存時に適用）
  if (values.questions.length > DivConst.items.questionCountLimit.voiceChat) {
    errors.push({ name: 'questions', message: '設問は10問以内で入力してください' });
  }

  // 設問（説明文のみ必須。回答モードに関わらず全設問で必須＝モック「必須」表示に忠実）
  values.questions.forEach((q, qi) => {
    if (isEmpty(q.title)) {
      errors.push({ name: `questions.${qi}.title`, message: '設問の説明文を入力してください' });
    }
  });

  // 完了ページ（単一）
  if (isHtmlFieldEmpty(values.completeDescriptionHtml)) {
    errors.push({
      name: 'completeDescriptionHtml',
      message: '完了ページの説明を入力してください',
    });
  }

  return errors;
}
